/**
 * Чтение данных экрана.
 *
 * Три честных состояния вместо «данные или undefined»: экран измерительного
 * продукта обязан уметь нарисовать ожидание в форме будущего содержимого и
 * сказать, что случилось с числом, — «пусто» и «не загрузилось» это разные
 * вещи, и путать их нельзя.
 *
 * Ключ запроса собирается строкой. Объект параметров в списке зависимостей
 * эффекта даёт новую ссылку на каждой перерисовке и превращает загрузку в
 * бесконечный цикл — ошибка, которую видно только по счётчику запросов.
 */

import { useCallback, useEffect, useState } from 'react';

import { isoDate } from '@shared/src/intl';

import { requestWith, type RequestOptions } from '../api/client';

export type Query = RequestOptions['query'];

export type Reading<T> =
  { kind: 'loading' } | { kind: 'ready'; data: T } | { kind: 'failed'; error: unknown };

/**
 * Состояние вместе с управлением. Разбор идёт по `kind` прямо на месте — экран
 * читается сверху вниз как «ждём → нарисовали → объяснили ошибку», без лишнего
 * уровня вложенности на каждом обращении.
 */
export type Read<T> = Reading<T> & {
  /** Перечитать с сервера: экран возвращается в ожидание. */
  reload: () => void;
  /**
   * Положить данные, пришедшие ответом на изменение, — без второго запроса.
   * Список после правки строки знает свой новый состав сам.
   */
  put: (data: T) => void;
};

/**
 * Один GET. `path = null` — грузить пока нечего (бренд не выбран), и это не
 * ошибка: экран остаётся в ожидании и сам объясняет, чего он ждёт.
 */
export function useRead<T>(path: string | null, query?: Query): Read<T> {
  const [attempt, setAttempt] = useState(0);
  const key = `${path ?? ''}?${JSON.stringify(query ?? {})}#${attempt}`;

  // Ответ хранится вместе с ключом, под который он получен. Ожидание тогда
  // **вычисляется** при отрисовке, а не выставляется эффектом: экран, который
  // сначала показывает чужие числа и только потом скелет, — это мигание, из-за
  // которого клиент успевает прочитать цифру прошлого бренда.
  const [held, setHeld] = useState<{ key: string; state: Reading<T> }>({
    key,
    state: { kind: 'loading' },
  });
  const state: Reading<T> = held.key === key ? held.state : { kind: 'loading' };

  useEffect(() => {
    if (!path) return;
    const abort = new AbortController();
    // Параметры разбираются из ключа: объект в зависимостях эффекта даёт новую
    // ссылку на каждой перерисовке и превращает загрузку в бесконечный цикл.
    const parsed = JSON.parse(key.slice(path.length + 1, key.lastIndexOf('#'))) as Query;

    requestWith<T>(path, { query: parsed, signal: abort.signal })
      .then(({ body }) => {
        if (abort.signal.aborted) return;
        setHeld({ key, state: { kind: 'ready', data: body } });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setHeld({ key, state: { kind: 'failed', error } });
      });

    return () => abort.abort();
  }, [key, path]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  const put = useCallback(
    (data: T) => setHeld({ key, state: { kind: 'ready', data } }),
    [key],
  );

  return { ...state, reload, put };
}

/** Курсорная страница контракта. `next_cursor: null` — данные кончились. */
export interface Page<T> {
  items: T[];
  next_cursor: string | null;
  limit: number;
}

/** Дата на N дней назад от сегодняшней даты рынка. */
export function daysAgo(days: number): string {
  const now = new Date();
  now.setUTCDate(now.getUTCDate() - days);
  return isoDate(now);
}
